import { useState, useEffect, useCallback } from 'react';
import screen from 'screen-size';
import { API_INSTANCE } from '../../../features/utils';
import { useSelector } from 'react-redux';
import { selectSiteData } from '../../../features/account-slice';
import { BsPaypal } from 'react-icons/bs';
import { SiSquare, SiStripe } from 'react-icons/si';
import { classes } from './connect-payment.st.css';
import { capitalize } from 'lodash';
import { Button as PrimeButton } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { Divider } from 'primereact/divider';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'src/redesign/components/Button';
import { getSelectedSiteDataSelector } from 'src/redesign/features/SelectedSite/store/selectors';
import { API_URL } from 'src/utils/transport';
import { useAppDispatch } from '../../../hooks';
import { showNotification } from 'src/redesign/features/Toast/store/actions';
import styles from './connect-payment.module.scss';

const SquareInfo = ({ account, deletePaymentConnector }) => {
  const [locations, setLocations] = useState<any>([]);
  const [locationLoading, setLocationLoading] = useState<any>(false);
  const [selectedLocation, setSelectedLocation] = useState<any>(account.location_id);
  const [savingLocation, setSavingLocation] = useState<any>(false);
  const [showSuccess, setShowSuccess] = useState<any>(false);
  const [locationsError, setLocationsError] = useState<any>(false);

  const site = useSelector(selectSiteData);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const squarePaymentLocationsUrl = `/api/v1/dashboard/payments/square/locations`;
        setLocationLoading(true);
        const { data } = await API_INSTANCE.get(squarePaymentLocationsUrl);
        setSelectedLocation(account.location_id);
        setLocations(data);
        setLocationLoading(false);
      } catch (e) {
        setLocationsError(true);
        setLocationLoading(false);
      }
    };
    void fetchData();
  }, [site]);

  const handleDropdown = useCallback(
    async (locationId) => {
      setSelectedLocation(locationId.value);
      setSavingLocation(true);
      const squarePaymentLocationsUrl = `/api/v1/dashboard/payments/square/locations`;
      await API_INSTANCE.put(squarePaymentLocationsUrl, {
        location_id: locationId.value,
      });
      setSavingLocation(false);
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 4000);
    },
    [selectedLocation, site],
  );

  const options = locations.map(({ id, name }) => ({ label: name, value: id }));

  return (
    <div className="flex flex-column gap-3">
      <div className={`${styles.paymentCard} ${styles.success}`}>
        <div className="flex flex-column gap-2">
          <div className="flex align-items-center justify-content-between">
            <div className="flex align-items-center gap-2">
              <div className={`${styles.iconCircle} ${styles.success}`}>
                <span>✓</span>
              </div>
              <h3 className={styles.cardTitle}>Square Account Connected</h3>
            </div>
            <Button priority="secondary" size="medium" onClick={deletePaymentConnector}>
              Disconnect
            </Button>
          </div>
          <p className={styles.cardText}>
            Merchant ID: <strong>{account?.merchant_id}</strong> • Country:{' '}
            <strong>{account?.country}</strong>
          </p>
        </div>
      </div>

      <div className="flex gap-2">
        <div className={styles.formField} style={{ flex: 1 }}>
          <label>Select Location</label>
          <Dropdown
            value={selectedLocation}
            options={options}
            onChange={handleDropdown}
            placeholder={locationsError ? 'Unable to Load Square Locations' : 'Select a location'}
            disabled={locationLoading || savingLocation}
            className="w-full"
          />
        </div>
        {showSuccess && (
          <div className="flex align-items-center justify-content-center">
            <img
              src="/assets/save-success-check.gif"
              className={styles.successCheck}
              alt="Success"
            />
          </div>
        )}
      </div>
    </div>
  );
};
const PayPalInfo = ({ account, deletePaymentConnector }) => {
  const isPaymentsReceivable = account?.merchant_status?.payments_receivable;

  return (
    <div className="flex flex-column gap-3">
      {isPaymentsReceivable ? (
        <div className={`${styles.paymentCard} ${styles.success}`}>
          <div className="flex flex-column gap-2">
            <div className="flex align-items-center justify-content-between">
              <div className="flex align-items-center gap-2">
                <div className={`${styles.iconCircle} ${styles.success}`}>
                  <span>✓</span>
                </div>
                <h3 className={styles.cardTitle}>PayPal Account Connected</h3>
              </div>
              <Button priority="secondary" size="medium" onClick={deletePaymentConnector}>
                Disconnect
              </Button>
            </div>
            <p className={styles.cardText}>
              Connected with <strong>{account?.email}</strong>. Your account is ready to accept
              payments.
            </p>
          </div>
        </div>
      ) : (
        <div className={`${styles.paymentCard} ${styles.warning}`}>
          <div className="flex flex-column gap-2">
            <div className="flex align-items-center justify-content-between">
              <div className="flex align-items-center gap-2">
                <div className={`${styles.iconCircle} ${styles.warning}`}>
                  <span>!</span>
                </div>
                <h3 className={styles.cardTitle}>Setup Required</h3>
              </div>
              <Button priority="secondary" size="medium" onClick={deletePaymentConnector}>
                Disconnect
              </Button>
            </div>
            <p className={styles.cardText}>
              PayPal account connected with <strong>{account?.email}</strong>, but additional setup
              is required.
            </p>
            <div className="mt-2">
              <a
                href="https://www.paypal.com/bizsignup/entry/product/ppcp"
                target="_blank"
                rel="noreferrer"
                className={styles.dangerLink}
              >
                Complete PayPal Business Setup →
              </a>
              <p className={`${styles.cardTextSmall} mt-1`}>
                Finish your PayPal Business account setup to enable credit card payments and receive
                funds.
              </p>
            </div>
          </div>
        </div>
      )}
      <Divider />
    </div>
  );
};

const StripeInfo = ({ account, fetchPaymentData, deletePaymentConnector }) => {
  const site = useSelector(selectSiteData);

  const dispatch = useAppDispatch();

  const [paymentMethods, setPaymentMethods] = useState({
    card: account?.payment_method_types?.card || false,
    ideal: account?.payment_method_types?.ideal || false,
    bancontact: account?.payment_method_types?.bancontact || false,
    p24: account?.payment_method_types?.p24 || false,
    eps: account?.payment_method_types?.eps || false,
    twint: account?.payment_method_types?.twint || false,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const togglePaymentMethod = (method) => {
    const updatedMethods = {
      ...paymentMethods,
      [method]: !paymentMethods[method],
    };
    const trueMethodsCount = Object.values(updatedMethods).filter(Boolean).length;
    if (!trueMethodsCount) {
      dispatch(
        showNotification({
          severity: 'error',
          summary: 'Error',
          detail: 'Please select at least one payment method to proceed.',
        }),
      );
      return;
    }
    setPaymentMethods(updatedMethods);
    setHasChanges(true);
  };

  const savePaymentMethods = async () => {
    try {
      setIsSaving(true);
      await API_INSTANCE.put(`/api/v1/dashboard/payments/stripe/methods`, {
        payment_method_types: paymentMethods,
      });
      console.log('Payment methods saved successfully.');
      dispatch(
        showNotification({
          severity: 'success',
          summary: 'Success',
          detail: 'Payment methods saved successfully',
        }),
      );
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving payment methods:', error);
      dispatch(
        showNotification({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to save payment methods',
        }),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const { requirements } = account.connected_account;

  const verificationDues =
    requirements?.currently_due.length !== 0 || requirements?.pending_verification.length !== 0;

  if (verificationDues) {
    return (
      <div className="flex flex-column gap-3">
        <div className={`${styles.paymentCard} ${styles.warning}`}>
          <div className="flex flex-column gap-2">
            <div className="flex align-items-center justify-content-between">
              <div className="flex align-items-center gap-2">
                <div className={`${styles.iconCircle} ${styles.warning}`}>
                  <span>!</span>
                </div>
                <h3 className={styles.cardTitle}>Setup Required</h3>
              </div>
              <Button priority="secondary" size="medium" onClick={deletePaymentConnector}>
                Disconnect
              </Button>
            </div>
            <p className={styles.cardText}>
              Stripe is missing some details to complete your account setup. Please provide the
              required information to start accepting payments.
            </p>
          </div>
        </div>

        <div className="flex gap-2 mt-2">
          <PrimeButton
            onClick={() => {
              const url = `${API_URL}/auth/stripe`;
              launchPopup(url, () => {
                void fetchPaymentData();
              });
            }}
            label="Complete Stripe Setup"
            severity="warning"
            icon="pi pi-plus"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-column gap-3">
      <div className={`${styles.paymentCard} ${styles.success}`}>
        <div className="flex flex-column gap-2">
          <div className="flex align-items-center justify-content-between">
            <div className="flex align-items-center gap-2">
              <div className={`${styles.iconCircle} ${styles.success}`}>
                <span>✓</span>
              </div>
              <h3 className={styles.cardTitle}>Stripe Account Connected</h3>
            </div>
            <Button priority="secondary" size="medium" onClick={deletePaymentConnector}>
              Disconnect
            </Button>
          </div>
          <p className={styles.cardText}>
            Connected with <strong>{account?.email}</strong>. Your account is ready to accept
            payments.
          </p>
        </div>
      </div>

      <div className={styles.paymentCard} style={{ marginTop: '1rem' }}>
        <div className="flex flex-column gap-3">
          <h4 className={styles.cardTitle} style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>
            Payment Methods
          </h4>
          <div className="flex flex-column gap-3">
            {Object.keys(paymentMethods).map((method) => (
              <div key={method} className={styles.paymentMethodRow}>
                <span>{capitalize(method)}</span>
                <InputSwitch
                  checked={paymentMethods[method]}
                  onChange={() => togglePaymentMethod(method)}
                />
              </div>
            ))}
          </div>
          {hasChanges && (
            <div className="flex justify-content-end mt-2">
              <Button
                priority="primary"
                size="medium"
                onClick={savePaymentMethods}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}
        </div>
      </div>

      <Divider />
    </div>
  );
};

export const Accounts = ({ account, fetchPaymentData, deletePaymentConnector }) => {
  return (
    <div className="flex flex-column gap-2">
      {account.type === 'square' && (
        <SquareInfo account={account} deletePaymentConnector={deletePaymentConnector} />
      )}
      {account.type === 'stripe' && (
        <StripeInfo
          account={account}
          fetchPaymentData={fetchPaymentData}
          deletePaymentConnector={deletePaymentConnector}
        />
      )}
      {account.type === 'paypal' && (
        <PayPalInfo account={account} deletePaymentConnector={deletePaymentConnector} />
      )}
    </div>
  );
};

const launchPopup = (url, onClose) => {
  let params = `width=${screen().x * 0.5}`;
  params += `, height=${screen().y * 0.8}`;
  params += ', top=0, left=0';
  params += ', fullscreen=yes';
  params += ', scrollbars=1';

  const newWin = window.open(url, 'Connect Payment', params);

  // Set up interval to check if popup is closed
  const checkPopup = setInterval(() => {
    if (newWin?.closed) {
      clearInterval(checkPopup);
      onClose?.();
    }
  }, 500);

  if (window.focus) {
    newWin.focus();
  }

  return newWin;
};

export const ConnectPaymentAccount = () => {
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const site = useSelector(getSelectedSiteDataSelector);

  const fetchPaymentData = async () => {
    try {
      setLoading(true);
      const url = `/api/v1/dashboard/payments`;
      const { data } = await API_INSTANCE.get(url);
      setPaymentDetails(data);
    } catch (error) {
      console.error('Error fetching payment details:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (site) {
      void fetchPaymentData();
    }
  }, [site]);

  const deletePaymentConnector = useCallback(async () => {
    try {
      const url = `/api/v1/dashboard/payments/${paymentDetails.type}/${paymentDetails.id}`;
      await API_INSTANCE.delete(url);
      setPaymentDetails(null);
    } catch (error) {
      console.error('Error deleting payment connector:', error);
    }
  }, [paymentDetails]);

  const handleConnectPayment = (provider) => {
    const url = `${API_URL}/auth/${provider}`;
    launchPopup(url, () => {
      // Fetch new payment data when popup closes
      void fetchPaymentData();
    });
  };

  if (loading) {
    return (
      <div className={styles.loaderContainer}>
        <ProgressSpinner style={{ width: '50px', height: '50px' }} />
      </div>
    );
  }

  return (
    <div className="flex flex-column">
      <div className="flex flex-column gap-2 mb-4">
        <h2 className={styles.cardTitle} style={{ fontSize: '1.5rem' }}>
          Setup Payment
        </h2>
        <p className={styles.cardText} style={{ fontSize: '0.9375rem' }}>
          Connect a payment provider to start accepting payments and receive payouts directly to
          your account
        </p>
      </div>
      <div className="flex flex-column mt-2">
        {paymentDetails ? (
          <div className="flex flex-column">
            <Accounts
              account={paymentDetails}
              deletePaymentConnector={deletePaymentConnector}
              fetchPaymentData={fetchPaymentData}
            />
          </div>
        ) : (
          <div className="flex gap-3 flex-wrap">
            <PrimeButton
              className={classes.stripe}
              icon={<SiStripe size={20} />}
              onClick={() => handleConnectPayment('stripe')}
              label="Connect Stripe"
              style={{
                height: '48px',
                fontSize: '1rem',
                fontWeight: '600',
                borderRadius: '6px',
                flex: '1 1 auto',
                minWidth: '180px',
              }}
            />
            <PrimeButton
              className={classes.square}
              icon={<SiSquare size={20} />}
              onClick={() => handleConnectPayment('square')}
              label="Connect Square"
              style={{
                height: '48px',
                fontSize: '1rem',
                fontWeight: '600',
                borderRadius: '6px',
                flex: '1 1 auto',
                minWidth: '180px',
              }}
            />
            <PrimeButton
              icon={<BsPaypal size={20} />}
              className={classes.paypal}
              onClick={() => handleConnectPayment('paypal')}
              label="Connect PayPal"
              style={{
                height: '48px',
                fontSize: '1rem',
                fontWeight: '600',
                borderRadius: '6px',
                flex: '1 1 auto',
                minWidth: '180px',
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
